import React, { useState, useEffect } from "react";
import ProductAPI from '../services/API';
import ProductForm from '../components/ProductForm';
import ProductList from '../components/ProductList';
import authService from '../services/authService';

const Admin = () => {  
  const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    description: "",
    photos: [],
    type: ""
  });
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('create');

  useEffect(() => {
    if (isAuthenticated) {
      fetchProducts();
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await authService.login(credentials);
      setIsAuthenticated(true);
      setError('');
    } catch (error) {
      setError('Invalid credentials');
    }
  };

  const fetchProducts = async () => {
    try {
      const data = await ProductAPI.getAllProducts();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'file' 
      ? Array.from(e.target.files)
      : e.target.value;

    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedProduct) {
        await ProductAPI.updateProduct(selectedProduct.id, formData);
      } else {
        await ProductAPI.createProduct(formData);
      }
      fetchProducts();
      resetForm();
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setFormData({
      id: product.id,
      name: product.name,
      price: product.price,
      description: product.description,
      type: product.type,
      photos: product.photos || []
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await ProductAPI.deleteProduct(id);
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const resetForm = () => {
    setSelectedProduct(null);
    setFormData({
      name: "",
      price: "",
      description: "",
      photos: [],
      type: ""
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded-lg shadow-md">
        <h2 className="text-2xl font-bold mb-6">Admin Login</h2>
        {error && <div className="text-red-500 mb-4">{error}</div>}
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block mb-1">Username</label>
            <input
              type="text"
              value={credentials.username}
              onChange={(e) => setCredentials({
                ...credentials,
                username: e.target.value
              })}
              className="w-full border p-2 rounded"
              required
            />
          </div>
          <div>
            <label className="block mb-1">Password</label>
            <input
              type="password"
              value={credentials.password}
              onChange={(e) => setCredentials({
                ...credentials,
                password: e.target.value
              })}
              className="w-full border p-2 rounded"
              required
            />
          </div>
          <button
            type="submit"
            className="w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
          >
            Login
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Admin Dashboard</h1>
        <button
          onClick={() => {
            authService.logout();
            setIsAuthenticated(false);
          }}
          className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
        >
          Logout
        </button>
      </div>
      
      <div className="mb-4">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => {
                setActiveTab('create');
                resetForm();
              }}
              className={`mr-8 py-2 px-4 ${
                activeTab === 'create'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {selectedProduct ? 'Edit Product' : 'Create Product'}
            </button>
            <button
              onClick={() => setActiveTab('manage')}
              className={`py-2 px-4 ${
                activeTab === 'manage'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Manage Products
            </button>
          </nav>
        </div>
      </div>

      {activeTab === 'create' ? (
        <ProductForm 
          formData={formData}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          isEditing={!!selectedProduct}
        />
      ) : (
        <ProductList 
          products={products}
          onEdit={(product) => {
            handleEdit(product);
            setActiveTab('create');
          }}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default Admin;